import { EnvironmentService } from '../services/environment/environment.service';
import { isDefined } from '../utils/variables';

export type Flag =
  | 'SK-2132-cloudprocessing-upload-override'
  | 'SK-4170-orthophoto-generation-from-tif'
  | 'SK-2638-react-skyview'
  | 'SK-3938-skymap-dongles'
  | 'SK-4477-access-management'
  | 'SK-5381-drone-harmony'
  | 'SK-5443-odm-pipeline'
  | 'SK-5515-new-gcp-page'
  | 'SK-5617-discontinue-cad-viewer';

export class FeatureFlagsManager {
  private static _instance: FeatureFlagsManager = new FeatureFlagsManager();
  private flags!: Record<Flag, boolean>;

  private constructor() {
    this.initialize();
    this.deleteFlagsFromProduction();
  }

  static get instance() {
    return FeatureFlagsManager._instance;
  }

  private initialize() {
    this.flags = {
      ...this.resolveFlag('SK-2132-cloudprocessing-upload-override', 'optional'),
      ...this.resolveFlag('SK-2638-react-skyview', 'optional'),
      ...this.resolveFlag('SK-3938-skymap-dongles', 'optional'),
      ...this.resolveFlag('SK-5443-odm-pipeline', 'optional'),
      ...this.resolveFlag('SK-5515-new-gcp-page', 'optional'),
      ...this.resolveFlag('SK-4170-orthophoto-generation-from-tif', 'optional'),
      ...this.resolveFlag('SK-4477-access-management', 'optional'),
      ...this.resolveFlag('SK-5381-drone-harmony', 'released'),
      ...this.resolveFlag('SK-5617-discontinue-cad-viewer', 'released'),
    };

    this.pushFeatureFlagsToNative();
  }

  /**
   * For a few months we hade NODE_ENV set to "development" in the production environment. This
   * resulted in all flags being created automatically in the production environment (should only be
   * created automatically in development environment).
   *
   * This function will delete affected feature flags from customers Local Storage, and only if
   * feature flag is not set to "true". If it's "true" customer has set it manually and the flag
   * should then not be deleted.
   *
   * Deleting these flags will prevent customers from easily enabling them by looking in Local
   * Storage.
   */
  private deleteFlagsFromProduction() {
    if (EnvironmentService.instance.isProduction()) {
      const flags: Flag[] = [
        'SK-2132-cloudprocessing-upload-override',
        'SK-2638-react-skyview',
        'SK-3938-skymap-dongles',
        'SK-4170-orthophoto-generation-from-tif',
        'SK-4477-access-management',
      ];
      for (const flag of flags) {
        const value = localStorage.getItem(flag);
        if (value !== 'true') {
          localStorage.removeItem(flag);
        }
      }
    }
  }

  /**
   * Returns a list of all feature flags and a boolean value indicating whether flag is enabled.
   */
  getAll() {
    return this.flags;
  }

  /**
   *  Returns a value indicating whether the specified flag is enabled.
   */
  isEnabled(flagName: Flag) {
    return this.flags[flagName];
  }

  /**
   *  Pushes the feature flags state to the native interface-
   */
  pushFeatureFlagsToNative() {
    // @ts-ignore
    window.iosCallbacks?.pushFeatureFlags();
  }

  /**
   * Returns a record with the specified flag and a value indicating whether the flag is enabled.
   * This enables us to spread it into the final Record containing all flags. Using this approach
   * we don't need to specify the flag twice when initializing the flags property.
   *
   * If parameter state is set to either released/disabled, this value will be used regardless of
   * whether the flag exists in Local Storage or not. Useful if flag needs to be reverted. Then
   * any customer added feature flag value will be overridden.
   *
   * Otherwise, the value is fetched from Local storage (optional). If flag doesn't exist in Local
   * Storage it's saved in Local Storage to false for non-production environments. This makes it
   * easier for developers and other colleagues to see what feature flags are available in developer
   * stacks and in development.
   * @param flag Name of feature flag.
   * @param state Whether the flag should be optional or forced released/disabled.
   */
  private resolveFlag<F extends Flag>(
    flag: F,
    state: 'optional' | 'released' | 'disabled',
  ): Record<F, boolean> {
    const value = state === 'optional' ? undefined : state === 'released';
    const savedValue = localStorage.getItem(flag);

    if (savedValue === null && !EnvironmentService.instance.isProduction()) {
      localStorage.setItem(flag, value?.toString() ?? 'false');
    }

    return {
      [flag]: isDefined(value) ? value : savedValue === 'true',
    } as Record<F, boolean>;
  }
}
